import { Application, Container, Sprite, Text, TextStyle, Texture } from 'pixi.js'
import { Spine } from '@esotericsoftware/spine-pixi-v8'
import { selectChickenStepMultiplier } from '@/store/slices/chickenSlice'
import type { ChickenDifficulty } from '@/types/chicken'
import { SoundManager } from './SoundManager'

const CHICKEN_START = { x: 218, y: 894, offsetY: 66 }
const ROAD_OVERLAP = 10
const FINISH_OVERLAP = 7
const JUMP_MS = 520
const DEATH_HOLD_MS = 900
const CAR_SPEED = 1.5
const FENCING_OFFSET_Y = 85
const CHICKEN_HEIGHT = 162
const LEVEL_HEIGHT = 1280
const ROAD_WIDTH = 190
const START_WIDTH = 309
const FINISH_WIDTH = 793
const CAMERA_ZOOM = 1.4

type RoadVisual = {
	container: Container
	width: number
	centerX: number
	fencing: Sprite
	coinGold: Sprite
	coinBronze: Container
	scoreText: Text
	scoreMulti: number
	car: Sprite | null
	spawnTimer: number
	nextSpawnAt: number
	isStopSpawnCar: boolean
}

function easeOutQuad(t: number) {
	return t * (2 - t)
}

function formatMultiplier(value: number) {
	return `${value.toFixed(2)}x`
}

export class ChickenScene {
	private readonly app: Application
	private readonly world = new Container()
	private readonly roads: RoadVisual[] = []
	private finishSprite: Sprite | null = null
	private chickenRoot: Container | null = null
	private chickenSpine: Spine | null = null
	private scoreContainer: Container | null = null
	private scoreText: Text | null = null
	private disposed = false
	private currentStep = 0
	private difficulty: ChickenDifficulty = 'easy'
	private multiplierSteps: number[] = []
	private levelWidth = START_WIDTH
	private nextRoadX = 0
	private cameraCenterX = CHICKEN_START.x
	private cameraCenterY = CHICKEN_START.y
	private resizeObserver: ResizeObserver | null = null

	constructor(app: Application) {
		this.app = app
		this.app.stage.addChild(this.world)
		this.world.sortableChildren = true
		this.createChicken()
		this.app.ticker.add(this.onTick, this)

		this.resizeObserver = new ResizeObserver(() => this.updateCamera())
		const parent = this.app.canvas.parentElement
		if (parent) {
			this.resizeObserver.observe(parent)
		}
	}

	dispose() {
		this.disposed = true
		this.app.ticker.remove(this.onTick, this)
		this.resizeObserver?.disconnect()
		this.resizeObserver = null
		this.world.destroy({ children: true, texture: false, textureSource: false })
	}

	rebuildLevel(difficulty: ChickenDifficulty, multiplierSteps: number[] = []) {
		this.clearRoadsAndBackdrop()
		this.difficulty = difficulty
		this.multiplierSteps = multiplierSteps
		this.currentStep = 0

		const start = new Sprite(Texture.from('bgStart'))
		start.zIndex = 1
		this.world.addChild(start)
		const startWidth = start.texture.width || START_WIDTH
		const finishWidth = Texture.from('bgFinish').width || FINISH_WIDTH
		this.nextRoadX = startWidth - ROAD_OVERLAP

		const statue = new Sprite(Texture.from('statuePhone'))
		statue.x = 25
		statue.y = 645
		statue.zIndex = 10
		this.world.addChild(statue)

		this.appendRoads(multiplierSteps.length)

		if (multiplierSteps.length > 0) {
			const finishX = this.nextRoadX - FINISH_OVERLAP
			const finish = new Sprite(Texture.from('bgFinish'))
			finish.x = finishX
			finish.zIndex = 1
			this.world.addChild(finish)
			this.finishSprite = finish

			const spring = new Sprite(Texture.from('springBoard'))
			spring.anchor.set(0, 0.5)
			spring.x = finish.x
			spring.y = 887
			spring.zIndex = 1
			this.world.addChild(spring)

			this.levelWidth = finishX + finishWidth
		} else {
			this.levelWidth = startWidth
		}

		this.resetRound()
		this.updateCamera()
	}

	setMultiplierTable(multiplierSteps: number[]) {
		this.multiplierSteps = multiplierSteps
		if (multiplierSteps.length > 0 && multiplierSteps.length !== this.roads.length) {
			this.rebuildLevel(this.difficulty, multiplierSteps)
			return
		}
		this.refreshRoadMultipliers()
	}

	resetRound() {
		this.currentStep = 0
		this.resetChickenVisual()
		this.resetRoadMarkers()
		this.highlightNextRoad()
		this.setMultiplierLabel(0)
		this.moveCameraTo(CHICKEN_START.x, CHICKEN_START.y, 0)
	}

	private resetRoadMarkers() {
		for (const road of this.roads) {
			this.removeCar(road)
			road.isStopSpawnCar = false
			road.spawnTimer = 0
			road.nextSpawnAt = 80 + Math.random() * 400
			road.fencing.visible = false
			road.fencing.alpha = 0
			road.fencing.y = CHICKEN_START.y - FENCING_OFFSET_Y * 5
			road.coinGold.visible = false
			road.coinGold.alpha = 0
			road.coinGold.scale.set(0, 1)
			road.coinBronze.visible = true
			road.coinBronze.alpha = 1
			road.coinBronze.scale.set(1, 1)
			road.scoreText.alpha = 0.7
		}

		this.refreshRoadMultipliers()
	}

	setChickenStep(step: number, animateCamera = true) {
		this.currentStep = Math.max(0, step)
		const x = this.xForStep(this.currentStep)
		if (this.chickenRoot) {
			this.chickenRoot.x = x
			this.chickenRoot.y = CHICKEN_START.y
		}

		for (let i = 0; i < this.roads.length; i++) {
			const crossed = i < this.currentStep
			this.roads[i].fencing.visible = crossed
			this.roads[i].fencing.alpha = crossed ? 1 : 0
			this.roads[i].fencing.y = CHICKEN_START.y - FENCING_OFFSET_Y
			this.roads[i].coinGold.visible = crossed
			this.roads[i].coinGold.alpha = crossed ? 1 : 0
			this.roads[i].coinGold.scale.set(crossed ? 1 : 0, 1)
			this.roads[i].coinBronze.visible = !crossed
			this.roads[i].coinBronze.alpha = 1
			this.roads[i].coinBronze.scale.set(1, 1)
			this.roads[i].isStopSpawnCar = crossed
			if (crossed) {
				this.removeCar(this.roads[i])
			}
		}

		this.highlightNextRoad()
		this.setIdle()
		this.moveCameraTo(x, CHICKEN_START.y, animateCamera ? 320 : 0)
	}

	setMultiplierLabel(multiplier: number) {
		if (!this.scoreText || !this.scoreContainer) {
			return
		}

		const value = multiplier > 0 ? multiplier : selectChickenStepMultiplier(this.multiplierSteps, this.currentStep)
		if (this.currentStep <= 0 || value <= 1) {
			this.scoreContainer.visible = false
			return
		}

		this.scoreText.text = formatMultiplier(value)
		this.scoreContainer.visible = true
	}

	playJumpPreview() {
		this.playSpine('jump', false)
		const next = this.roads[this.currentStep]
		if (next) {
			next.coinBronze.visible = true
			next.scoreText.alpha = 1
		}
	}

	async playSurviveToStep(step: number) {
		const target = Math.max(1, step)
		const road = this.roads[target - 1]
		if (!road || !this.chickenRoot) {
			this.setChickenStep(target, true)
			return
		}

		this.scoreContainer && (this.scoreContainer.visible = false)
		this.playSpine('jump', false)
		road.coinBronze.scale.set(0, 1)
		road.coinBronze.visible = false
		road.coinBronze.alpha = 0
		this.moveCameraTo(road.centerX, CHICKEN_START.y, JUMP_MS)
		await this.moveChickenTo(road.centerX, JUMP_MS)
		if (this.disposed) {
			return
		}

		road.isStopSpawnCar = true
		road.fencing.visible = true
		road.fencing.alpha = 1
		road.fencing.y = CHICKEN_START.y - FENCING_OFFSET_Y
		this.holdCarAtFence(road)
		road.coinGold.visible = true
		road.coinGold.alpha = 1
		road.coinGold.scale.set(1, 1)

		this.currentStep = target
		this.highlightNextRoad()
		this.setMultiplierLabel(road.scoreMulti)
		this.setIdle()
	}

	async playDeathAtStep(step: number) {
		const target = Math.max(1, step)
		const road = this.roads[target - 1]
		if (!road || !this.chickenRoot) {
			return
		}

		this.scoreContainer && (this.scoreContainer.visible = false)
		this.playSpine('jump', false)
		this.moveCameraTo(road.centerX, CHICKEN_START.y, JUMP_MS)

		const carPromise = this.spawnHitCar(road)
		await Promise.all([this.moveChickenTo(road.centerX, JUMP_MS * 0.85), carPromise])
		if (this.disposed) {
			return
		}

		SoundManager.instance.play('death')
		this.playSpine('death', false)
		this.currentStep = target
		await this.delay(DEATH_HOLD_MS)
	}

	async playFinishWin() {
		if (!this.chickenRoot || !this.finishSprite) {
			this.playSpine('win', false)
			await this.delay(700)
			return
		}

		const finishX = this.finishSprite.x
		const goldPoint = { x: finishX + 315, y: CHICKEN_START.y + 20 }
		const finishPoint = { x: finishX + 150, y: CHICKEN_START.y - 24 }

		this.scoreContainer && (this.scoreContainer.visible = false)
		this.moveCameraTo(goldPoint.x, goldPoint.y, 500)

		const fromY = this.chickenRoot.y
		const fromX = this.chickenRoot.x
		await Promise.all([
			this.animate(350, (t) => {
				if (!this.chickenRoot) {
					return
				}

				const e = easeOutQuad(t)
				this.chickenRoot.x = fromX + (finishPoint.x - fromX) * e
				this.chickenRoot.y = fromY + (finishPoint.y - fromY) * e
			}),
			this.delay(50).then(() => {
				this.playSpine('win', false)
			}),
		])

		if (this.chickenRoot) {
			this.chickenRoot.x = finishPoint.x
			this.chickenRoot.y = finishPoint.y
		}

		this.resetRoadMarkers()
		await this.delay(700)
	}

	private createChicken() {
		const root = new Container()
		root.zIndex = 5
		root.x = CHICKEN_START.x
		root.y = CHICKEN_START.y

		try {
			const spine = Spine.from({
				skeleton: 'chickenSpine',
				atlas: 'chickenSpineAtlas',
				scale: 0.62,
			})
			spine.y = CHICKEN_START.offsetY
			spine.state.setAnimation(0, 'idle', true)
			root.addChild(spine)
			this.chickenSpine = spine
		} catch (error) {
			console.error('Chicken spine failed to initialize', error)
			const fallback = new Sprite(Texture.from('chickenStatic'))
			fallback.anchor.set(0.5, 1)
			fallback.y = CHICKEN_START.offsetY
			root.addChild(fallback)
		}

		const scoreText = new Text({
			text: formatMultiplier(1.01),
			style: new TextStyle({
				fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
				fontSize: 28,
				fontWeight: '700',
				fill: '#fff5e6',
				dropShadow: {
					color: '#000000',
					angle: Math.PI / 2,
					distance: 2,
				},
			}),
		})
		scoreText.anchor.set(0.5, 0)
		scoreText.y = 26
		scoreText.x = 4

		const scoreBg = new Sprite(Texture.from('chickenScore'))
		scoreBg.anchor.set(0.5, 0)

		const scoreContainer = new Container()
		scoreContainer.x = -10
		scoreContainer.y = 66
		scoreContainer.visible = false
		scoreContainer.addChild(scoreBg, scoreText)
		root.addChild(scoreContainer)

		this.chickenRoot = root
		this.scoreContainer = scoreContainer
		this.scoreText = scoreText
		this.world.addChild(root)
	}

	private appendRoads(count: number) {
		for (let i = 0; i < count; i++) {
			const index = this.roads.length
			const road = this.createRoad(index, this.nextRoadX)
			this.roads.push(road)
			this.world.addChild(road.container)
			this.nextRoadX += road.width
		}
	}

	private refreshRoadMultipliers() {
		for (let i = 0; i < this.roads.length; i++) {
			const scoreMulti = selectChickenStepMultiplier(this.multiplierSteps, i + 1)
			this.roads[i].scoreMulti = scoreMulti
			this.roads[i].scoreText.text = scoreMulti > 0 ? formatMultiplier(scoreMulti) : '—'
		}
	}

	private createRoad(index: number, x: number): RoadVisual {
		const container = new Container()
		container.x = x
		container.zIndex = 0
		container.sortableChildren = true

		const bg = new Sprite(Texture.from('bgRoad'))
		container.addChild(bg)

		const width = bg.texture.width || ROAD_WIDTH
		const centerX = x + width / 2
		const scoreMulti = selectChickenStepMultiplier(this.multiplierSteps, index + 1)

		const scoreText = new Text({
			text: scoreMulti > 0 ? formatMultiplier(scoreMulti) : '—',
			style: new TextStyle({
				fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
				fontSize: 30,
				fontWeight: '700',
				fill: '#fff5e6',
				stroke: { width: 4, color: '#a8742f' },
				dropShadow: {
					color: '#000000',
					angle: Math.PI / 2,
					distance: 2,
				},
			}),
		})
		scoreText.anchor.set(0.5)
		scoreText.y = -2
		scoreText.alpha = 0.7

		const coinBronze = new Container()
		coinBronze.x = width / 2
		coinBronze.y = CHICKEN_START.y
		const bronzeSprite = new Sprite(Texture.from('coinBronze'))
		bronzeSprite.anchor.set(0.5)
		coinBronze.addChild(bronzeSprite, scoreText)
		container.addChild(coinBronze)

		const coinGold = new Sprite(Texture.from('coinGold'))
		coinGold.anchor.set(0.5)
		coinGold.x = width / 2
		coinGold.y = CHICKEN_START.y
		coinGold.scale.set(0, 1)
		coinGold.alpha = 0
		coinGold.visible = false
		container.addChild(coinGold)

		const fencing = new Sprite(Texture.from('fencing'))
		fencing.anchor.set(0.5, 1)
		fencing.x = width / 2
		fencing.y = CHICKEN_START.y - FENCING_OFFSET_Y * 5
		fencing.alpha = 0
		fencing.visible = false
		fencing.zIndex = 6
		container.addChild(fencing)

		return {
			container,
			width,
			centerX,
			fencing,
			coinGold,
			coinBronze,
			scoreText,
			scoreMulti,
			car: null,
			spawnTimer: 0,
			nextSpawnAt: 80 + Math.random() * 400,
			isStopSpawnCar: false,
		}
	}

	private highlightNextRoad() {
		for (let i = 0; i < this.roads.length; i++) {
			this.roads[i].scoreText.alpha = i === this.currentStep ? 1 : 0.7
		}
	}

	private clearRoadsAndBackdrop() {
		this.roads.length = 0
		this.finishSprite = null
		this.nextRoadX = 0

		for (const child of [...this.world.children]) {
			if (child !== this.chickenRoot) {
				child.destroy({ children: true, texture: false, textureSource: false })
			}
		}
	}

	private resetChickenVisual() {
		if (!this.chickenRoot) {
			return
		}

		this.chickenRoot.x = CHICKEN_START.x
		this.chickenRoot.y = CHICKEN_START.y
		this.scoreContainer && (this.scoreContainer.visible = false)
		this.setIdle()
	}

	private xForStep(step: number) {
		if (step <= 0) {
			return CHICKEN_START.x
		}

		return this.roads[step - 1]?.centerX ?? CHICKEN_START.x
	}

	private setIdle() {
		if (!this.chickenSpine) {
			return
		}

		try {
			this.chickenSpine.state.setAnimation(0, 'idle', true)
		} catch {
			// Spine track unavailable.
		}
	}

	private playSpine(name: string, loop: boolean) {
		if (!this.chickenSpine) {
			return
		}

		try {
			const track = this.chickenSpine.state.setAnimation(0, name, loop)
			if (name === 'jump' && track) {
				track.timeScale = 1.35
			}
		} catch {
			// Spine track unavailable.
		}
	}

	private async moveChickenTo(x: number, durationMs: number) {
		if (!this.chickenRoot) {
			return
		}

		const from = this.chickenRoot.x
		await this.animate(durationMs, (t) => {
			if (!this.chickenRoot) {
				return
			}

			this.chickenRoot.x = from + (x - from) * easeOutQuad(t)
		})

		if (this.chickenRoot) {
			this.chickenRoot.x = x
		}
	}

	private async spawnHitCar(road: RoadVisual) {
		this.removeCar(road)
		road.isStopSpawnCar = true

		const car = new Sprite(Texture.from(`car${Math.floor(Math.random() * 4)}`))
		car.anchor.set(0.5, 1)
		car.x = road.width / 2
		car.y = -40
		car.zIndex = 4
		road.container.addChild(car)
		road.car = car

		const from = -40
		const to = CHICKEN_START.y
		await this.animate(420, (t) => {
			car.y = from + (to - from) * t
		})
	}

	private spawnCar(road: RoadVisual) {
		if (road.car || road.isStopSpawnCar || road.fencing.visible) {
			return
		}

		const car = new Sprite(Texture.from(`car${Math.floor(Math.random() * 4)}`))
		car.anchor.set(0.5, 1)
		car.x = road.width / 2
		car.y = -5
		car.zIndex = 4
		road.container.addChild(car)
		road.car = car
	}

	private fenceStopY() {
		return CHICKEN_START.y - FENCING_OFFSET_Y
	}

	private isCarPastChicken(car: Sprite) {
		return car.y >= CHICKEN_START.y + CHICKEN_HEIGHT
	}

	private holdCarAtFence(road: RoadVisual) {
		if (!road.car || this.isCarPastChicken(road.car)) {
			return
		}

		const stopY = this.fenceStopY()
		if (road.car.y > stopY) {
			road.car.y = stopY
		}
	}

	private removeCar(road: RoadVisual) {
		if (!road.car) {
			return
		}

		road.car.destroy({ children: true, texture: false, textureSource: false })
		road.car = null
	}

	private onTick() {
		if (this.disposed) {
			return
		}

		const dt = this.app.ticker.deltaMS
		this.updateCars(dt)
		this.updateCamera()
	}

	private updateCars(dt: number) {
		for (const road of this.roads) {
			if (road.car) {
				const car = road.car

				if (road.fencing.visible) {
					road.isStopSpawnCar = true

					if (this.isCarPastChicken(car)) {
						car.y += CAR_SPEED * dt
						if (car.y > LEVEL_HEIGHT + (car.height || 0) + 5) {
							this.removeCar(road)
						}
						continue
					}

					car.y = Math.min(car.y + CAR_SPEED * dt, this.fenceStopY())
					continue
				}

				car.y += CAR_SPEED * dt

				const exitY = LEVEL_HEIGHT + (car.height || 0) + 5
				if (car.y > exitY) {
					this.removeCar(road)
				}
				continue
			}

			if (road.isStopSpawnCar || road.fencing.visible) {
				continue
			}

			road.spawnTimer += dt
			if (road.spawnTimer >= road.nextSpawnAt) {
				road.spawnTimer = 0
				road.nextSpawnAt = 120 + Math.random() * 520
				this.spawnCar(road)
			}
		}
	}

	private moveCameraTo(x: number, y: number, durationMs: number) {
		if (durationMs <= 0) {
			this.cameraCenterX = x
			this.cameraCenterY = y
			this.updateCamera()
			return
		}

		const fromX = this.cameraCenterX
		const fromY = this.cameraCenterY
		void this.animate(durationMs, (t) => {
			const e = easeOutQuad(t)
			this.cameraCenterX = fromX + (x - fromX) * e
			this.cameraCenterY = fromY + (y - fromY) * e
		})
	}

	private updateCamera() {
		const viewW = this.app.screen.width
		const viewH = this.app.screen.height
		if (viewW <= 0 || viewH <= 0) {
			return
		}

		const scale = (viewH / LEVEL_HEIGHT) * CAMERA_ZOOM
		this.world.scale.set(scale)

		const targetX = viewW / 2 - this.cameraCenterX * scale
		const targetY = viewH / 2 - this.cameraCenterY * scale
		const minX = viewW - this.levelWidth * scale
		const minY = viewH - LEVEL_HEIGHT * scale

		this.world.x = Math.max(minX, Math.min(0, targetX))
		this.world.y = Math.max(minY, Math.min(0, targetY))
	}

	private animate(durationMs: number, onUpdate: (t: number) => void) {
		return new Promise<void>((resolve) => {
			const start = performance.now()
			const tick = (now: number) => {
				if (this.disposed) {
					resolve()
					return
				}

				const t = Math.min(1, (now - start) / durationMs)
				onUpdate(t)
				if (t < 1) {
					requestAnimationFrame(tick)
				} else {
					resolve()
				}
			}

			requestAnimationFrame(tick)
		})
	}

	private delay(ms: number) {
		return new Promise<void>((resolve) => {
			window.setTimeout(resolve, ms)
		})
	}
}
